/**
 * Utility functions for handling dates and Unix timestamps
 */

const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseDateOnlyString = (value: string): Date | null => {
  const match = value.match(DATE_ONLY_REGEX);
  if (!match) return null;

  const [, yearStr, monthStr, dayStr] = match;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  const date = new Date(year, month - 1, day);

  if (
    isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

/**
 * Converts a Unix timestamp (in seconds) to a Date object
 * @param timestamp - Unix timestamp in seconds (10 digits) or milliseconds (13 digits)
 * @returns Date object or null if invalid
 */
export const timestampToDate = (
  timestamp: number | string | undefined,
): Date | null => {
  if (!timestamp) return null;

  const numTimestamp =
    typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;

  if (isNaN(numTimestamp)) return null;

  // If timestamp has 10 digits, it's in seconds - convert to milliseconds
  // If timestamp has 13 digits, it's already in milliseconds
  const timestampMs =
    numTimestamp.toString().length === 10 ? numTimestamp * 1000 : numTimestamp;

  const date = new Date(timestampMs);

  // Check if the date is valid
  if (isNaN(date.getTime())) return null;

  return date;
};

/**
 * Converts a Date object to a Unix timestamp in seconds
 * @param date - Date object
 * @returns Unix timestamp in seconds or null if invalid
 */
export const dateToTimestamp = (
  date: Date | null | undefined,
): number | null => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;

  return Math.floor(date.getTime() / 1000);
};

/**
 * Ensures a value is a valid Date object
 * Handles conversion from Unix timestamps if needed
 * @param dateValue - Date, timestamp, or string
 * @returns Date object or null if invalid
 */
export const ensureValidDate = (dateValue: any): Date | null => {
  if (!dateValue) return null;

  // Already a Date object
  if (dateValue instanceof Date) {
    return isNaN(dateValue.getTime()) ? null : dateValue;
  }

  // Number (timestamp)
  if (typeof dateValue === 'number') {
    return timestampToDate(dateValue);
  }

  // String (could be timestamp or date string)
  if (typeof dateValue === 'string') {
    // Only treat as a numeric timestamp if the ENTIRE string is digits.
    // parseInt("2026-04-01") returns 2026, which would be wrongly treated
    // as a Unix timestamp in seconds and land at 01/01/1970.
    if (/^\d+$/.test(dateValue)) {
      const numValue = parseInt(dateValue, 10);
      if (!isNaN(numValue)) {
        return timestampToDate(numValue);
      }
    }

    // Preserve simple date-only strings in local time so the selected day
    // does not shift backward for users in negative UTC offsets.
    const localDateOnly = parseDateOnlyString(dateValue);
    if (localDateOnly) {
      return localDateOnly;
    }

    // Parse other ISO/date strings (e.g. full timestamps) normally.
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
};

/**
 * Formats a date for display
 * @param dateValue - Date, timestamp, or string
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string or empty string if invalid
 */
export const formatDate = (
  dateValue: any,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
): string => {
  const date = ensureValidDate(dateValue);
  if (!date) return '';

  return date.toLocaleDateString('en-US', options);
};

/**
 * Formats a timestamp for display (common format used throughout the app)
 * @param timestamp - Unix timestamp in seconds
 * @returns Formatted date string or '-' if invalid
 */
export const formatTimestamp = (timestamp: number | undefined): string => {
  if (!timestamp) return '-';

  const date = timestampToDate(timestamp);
  if (!date) return '-';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Formats a dateValue into an ISO date string (YYYY-MM-DD)
 * @param dateValue - Date, timestamp, or string
 * @returns YYYY-MM-DD string or empty string if invalid
 */
export const toISODateString = (dateValue: any): string => {
  const date = ensureValidDate(dateValue);
  if (!date) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};
